import React, { useEffect, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { Link, useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { lang } from "../../../middleware/lang";
import { LoaderDots } from "../../../middleware/loaders";
import { GET_PURCHASE_ORDER, RECEIVE_PURCHASE_ORDER } from "./queries";
import { PurchaseOrderItem } from "./models";

type Params = { id: string };

interface ReceiveRow {
  item_id: string;
  ingredient_name: string;
  unit: string;
  quantity_ordered: number;
  already_received: number;
  already_damaged: number;
  quantity_received: string;
  quantity_damaged: string;
  notes: string;
}

const ReceiveForm: React.FC = () => {
  const { id } = useParams<Params>();
  const history = useHistory();
  const [rows, setRows] = useState<ReceiveRow[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const [fetchOrder, { data, loading }] = useLazyQuery(GET_PURCHASE_ORDER, {
    fetchPolicy: "no-cache",
  });
  const [receivePO] = useMutation(RECEIVE_PURCHASE_ORDER);

  useEffect(() => {
    fetchOrder({ variables: { id } });
    //eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (data?.getPurchaseOrder?.items) {
      const items: PurchaseOrderItem[] = data.getPurchaseOrder.items;
      setRows(
        items.map((item) => ({
          item_id: item.id,
          ingredient_name:
            item.ingredient?.name || `#${item.ingredient_id}`,
          unit: item.unit,
          quantity_ordered: item.quantity_ordered,
          already_received: item.quantity_received || 0,
          already_damaged: item.quantity_damaged || 0,
          quantity_received: "",
          quantity_damaged: "",
          notes: "",
        }))
      );
    }
  }, [data]);

  const updateRow = (index: number, field: string, value: string) => {
    const updated = [...rows];
    updated[index] = { ...updated[index], [field]: value };
    setRows(updated);
  };

  const handleReceive = async () => {
    const receiveItems = rows
      .filter((r) => r.quantity_received && parseFloat(r.quantity_received) > 0)
      .map((r) => ({
        item_id: parseInt(r.item_id),
        quantity_received: parseFloat(r.quantity_received),
        quantity_damaged: r.quantity_damaged
          ? parseFloat(r.quantity_damaged)
          : undefined,
        notes: r.notes || undefined,
      }));

    if (receiveItems.length === 0) {
      toast.error(
        lang(
          "Ingresa al menos una cantidad recibida",
          "Enter at least one received quantity"
        )
      );
      return;
    }

    setSubmitting(true);
    try {
      await receivePO({
        variables: { id, items: receiveItems },
      });
      toast.success(lang("Entrega registrada", "Delivery recorded"));
      history.push(`/inventory/purchases/${id}`);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoaderDots fullSize />;
  if (!data?.getPurchaseOrder) return null;

  const po = data.getPurchaseOrder;

  return (
    <>
      <p>
        <Link to={`/inventory/purchases/${id}`}>
          <i className="fa fa-chevron-left" aria-hidden="true"></i> OC-{id}
        </Link>
      </p>
      <h1>
        <i className="fas fa-truck mr-2"></i>
        {lang("Recibir Entrega", "Receive Delivery")} — OC-{id}
      </h1>

      <div className="row mb-3">
        <div className="col-lg-4">
          <div className="material_card p-3">
            <p className="mb-1">
              <strong>{lang("Proveedor", "Supplier")}:</strong>{" "}
              {po.supplier?.name}
            </p>
            <p className="mb-0">
              <strong>{lang("Sucursal", "Branch")}:</strong>{" "}
              {po.branch?.name}
            </p>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12">
          <div className="material_card p-3 mb-3">
            <div className="table-responsive">
              <table className="table table-sm">
                <thead className="thead-light">
                  <tr>
                    <th>{lang("Ingrediente", "Ingredient")}</th>
                    <th className="text-center">
                      {lang("Ordenado", "Ordered")}
                    </th>
                    <th className="text-center">
                      {lang("Ya recibido", "Already received")}
                    </th>
                    <th className="text-center">
                      {lang("Pendiente", "Pending")}
                    </th>
                    <th style={{ width: 120 }}>
                      {lang("Recibido ahora", "Received now")}
                    </th>
                    <th style={{ width: 120 }}>
                      {lang("Dañado", "Damaged")}
                    </th>
                    <th style={{ width: 150 }}>
                      {lang("Notas", "Notes")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => {
                    const pending =
                      row.quantity_ordered -
                      row.already_received -
                      row.already_damaged;

                    return (
                      <tr key={row.item_id}>
                        <td className="font-weight-bold">
                          {row.ingredient_name}
                          <br />
                          <small className="text-muted">{row.unit}</small>
                        </td>
                        <td className="text-center">
                          {Number(row.quantity_ordered).toFixed(2)}
                        </td>
                        <td className="text-center">
                          {row.already_received > 0 ? (
                            <span className="text-success">
                              {Number(row.already_received).toFixed(2)}
                            </span>
                          ) : (
                            "0"
                          )}
                        </td>
                        <td className="text-center">
                          <span
                            className={
                              pending > 0 ? "text-warning font-weight-bold" : "text-success"
                            }
                          >
                            {Number(pending).toFixed(2)}
                          </span>
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={row.quantity_received}
                            onChange={(e) =>
                              updateRow(index, "quantity_received", e.target.value)
                            }
                            step="0.01"
                            min="0"
                            max={pending}
                            placeholder="0"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={row.quantity_damaged}
                            onChange={(e) =>
                              updateRow(index, "quantity_damaged", e.target.value)
                            }
                            step="0.01"
                            min="0"
                            placeholder="0"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={row.notes}
                            onChange={(e) =>
                              updateRow(index, "notes", e.target.value)
                            }
                            placeholder="—"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-end mt-3">
              <Link
                to={`/inventory/purchases/${id}`}
                className="btn btn-outline-secondary mr-2"
              >
                {lang("Cancelar", "Cancel")}
              </Link>
              <button
                className="btn btn-success"
                onClick={handleReceive}
                disabled={submitting}
              >
                {submitting ? (
                  <span className="spinner-border spinner-border-sm mr-1"></span>
                ) : (
                  <i className="fas fa-check mr-1"></i>
                )}
                {lang("Confirmar Recepción", "Confirm Receipt")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReceiveForm;
